import { useEffect, useRef, useState } from 'react';

/**
 * [MOTION-A 2026-08-20] Exit animations for conditionally-rendered UI.
 *
 * React unmounts `{open && <Modal/>}` the instant `open` flips false, so a close can never
 * animate — the popup simply vanishes. This hook keeps the element mounted for `exitMs`
 * after `open` turns false (long enough for its CSS exit to play), and reports `entered`
 * one frame after mount so a CSS *transition* has a from-state to animate out of (an
 * element that mounts straight into its final state never transitions in).
 *
 * `value` retains the last truthy input while exiting, so a modal opened for a record keeps
 * rendering THAT record while it animates away rather than flashing empty.
 *
 * Usage (parent):
 *   const pres = usePresence(selectedRow, durationMs.fast);
 *   {pres.mounted && pres.value && <Modal row={pres.value} closing={pres.closing} … />}
 *
 * Respects prefers-reduced-motion: the exit hold collapses to 0 so nothing lingers when the
 * global GlobalStyles rule has already zeroed every animation.
 */
export interface Presence<T> {
  mounted: boolean;   // render the element at all (true in the same render that opens — [FLUID-3 2026-10-12])
  entered: boolean;   // true one frame after mount while open, false the moment it closes — drive "open" transitions from this
  closing: boolean;   // mounted but no longer open — play the exit animation
  value: T;           // the last truthy input (stable through the exit)
}

const prefersReducedMotion = (): boolean => {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

export function usePresence<T>(value: T, exitMs: number): Presence<T> {
  const open = !!value;
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);
  const lastRef = useRef<T>(value);
  if (value) lastRef.current = value;
  // [FLUID-3 2026-10-12] Mount in the SAME render that opens: a render-phase update (React re-runs this component
  // before it commits — no extra commit). It used to wait for an effect, which cost a whole extra render of the
  // component holding the hook (often a big page: measured ~13 ms of rendering + ~75 ms of waiting on Inventory with
  // 300 rows, dev build) before the window even existed.
  if (open && !mounted) setMounted(true);

  useEffect(() => {
    if (open) {
      // [FLUID-3 2026-10-12] ONE frame (was two): the state set inside the frame's callback is rendered by React in
      // a task AFTER that frame, and the frame itself has already computed the from-state (the closed styles of the
      // element just mounted) — so the transition always has its from-state, and opens ~16 ms sooner.
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }
    if (!mounted) return undefined;
    const hold = prefersReducedMotion() ? 0 : exitMs;
    // [FLUID-3 2026-10-12] `entered` is reset together with the unmount (one render), not when the close starts:
    // the close already shows at once through the returned `entered` below.
    const t = setTimeout(() => { setMounted(false); setEntered(false); }, hold);
    return () => clearTimeout(t);
  }, [open, mounted, exitMs]);

  // [FLUID-3 2026-10-12] `entered` only while open, so a close starts in the very render that closes (it used to
  // take one more render, from an effect). Re-opened during its close, it heads straight back open from where it is.
  return { mounted: mounted || open, entered: open && entered, closing: mounted && !open, value: lastRef.current };
}
